/**
 * Diacritic-insensitive matching for Vietnamese place names.
 *
 * Stop and route names come from the operator's data and are fully accented
 * (`Cầu Giấy`, `Đống Đa`), but nobody types accents into a search field on a
 * phone. Matching folds both sides to unaccented lowercase.
 */

/**
 * NFD decomposes most Vietnamese vowels into a base letter plus combining marks,
 * which the regex below then strips. It does NOT touch `Đ`/`đ` (U+0110/U+0111):
 * those are distinct letters with a stroke, not a base letter plus a mark, and
 * have no canonical decomposition. Verified against the Unicode data — `ầ` and
 * `ấ` decompose to 3 code points and `ơ` to 2, while `Đ` and `đ` stay at 1.
 *
 * Without this map, a user typing `dong da` finds nothing for `Đống Đa`. It is
 * the single most likely search bug in this codebase.
 */
const STROKED_D = /[Đđ]/g

const COMBINING_MARKS = /[̀-ͯ]/g

export function normalizeForSearch(value: string): string {
  return value
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .replace(STROKED_D, (character) => (character === 'Đ' ? 'D' : 'd'))
    .toLocaleLowerCase()
    .trim()
}

/**
 * Whether `haystack` contains `needle`, both normalised.
 *
 * Takes an already-normalised haystack so callers can precompute it once per
 * record — normalising 4,991 stop names on every keystroke is the obvious
 * implementation and the wrong one.
 */
export function matchesNormalized(normalizedHaystack: string, needle: string): boolean {
  const query = normalizeForSearch(needle)
  if (!query) return true
  return normalizedHaystack.includes(query)
}
